import classNames from 'classnames'
import React from 'react'
import { Card, Cards, Column, Grid } from 'src/components/framework/ui'
import {
  PrimaryNav,
  PrimaryNavItem,
  PrimaryNavLabel,
  PrimaryNavLink,
  PrimaryNavList,
} from 'src/components/framework/ui/primary-nav'
import { BasicRatios } from 'src/components/page/ratios/basic/basic-ratios'
import { StockRanks } from 'src/components/page/ratios/stock-ranks/stock-ranks'
import { usePageContext } from 'src/contexts/page.context'
import { useSecurityContext } from 'src/contexts/security.context'
import { Quote } from 'src/shared/quote.dto'
import { Security } from 'src/shared/security.dto'
import { makeSecurityLink } from 'src/shared/utils'

import { SecurityDetails } from './security-details'

export interface SecurityInfoProps {
  quote?: Quote
  className?: string
}

export const SecurityInfoContainer: React.FC<SecurityInfoProps> = ({
  className = '',
}) => {
  const { selectedNavIndex } = usePageContext()
  const { security } = useSecurityContext()
  const { marketCap, enterpriseValue, revenue } = security.ratios.general

  return (
    <Cards
      variants={['floating']}
      className={classNames('stk-container', className)}
    >
      <Card>
        <Grid>
          <Column msm={12} ts={6}>
            <SecurityDetails />
          </Column>
          <Column msm={12} ts={6}>
            <Grid>
              <Column msm={12} ts={6}>
                <BasicRatios
                  marketCap={marketCap}
                  enterpriseValue={enterpriseValue}
                  revenue={revenue}
                />
              </Column>
              <Column msm={12} ts={6}>
                <StockRanks />
              </Column>
            </Grid>
          </Column>
        </Grid>
      </Card>
      <Card
        className={`stk-space-in-t-0 stk-space-in-b-0 stk-hide-msm`}
        style={{ position: 'relative', zIndex: 1 }}
      >
        {buildNavigation(security, selectedNavIndex)}
      </Card>
    </Cards>
  )
}

function buildNavigationTree(
  security: Security,
  selectedIndex: number,
): NavigationItem[] {
  const googleTicker = `${security.googleExchangeCode}:${security.ticker}`
  const sharePricesUrl = makeSecurityLink(security)

  return [
    { label: 'StockReport™', href: '.' },
    {
      label: 'Tools',
      children: [
        { label: 'Compare with', href: `/compare/?tickers=${googleTicker}` },
        {
          label: 'Run through a checklist',
          href: `/checklists/?tickers=${googleTicker}`,
        },
      ],
    },
    {
      label: 'Accounts',
      children: [
        { label: 'Balance sheet', href: `${sharePricesUrl}/balance_sheet/` },
        {
          label: 'Income statement',
          href: `${sharePricesUrl}/income_statement/`,
        },
        { label: 'Cashflow statement', href: `${sharePricesUrl}/cash_flow/` },
        { label: 'Dividend summary', href: `${sharePricesUrl}/dividends/` },
      ],
    },
    { label: 'Charts', href: `${sharePricesUrl}/chart/` },
    { label: 'News', href: `${sharePricesUrl}/news/` },
    { label: 'Discussion', href: `${sharePricesUrl}/discussion/` },
    {
      label: `${security.ticker} Dividends`,
      href: `${sharePricesUrl}/dividends/`,
    },
  ].map((item, index) => ({ ...item, selected: index === selectedIndex }))
}

function buildNavigationItems(data: NavigationItem[]) {
  return data.map((item, index) => (
    <PrimaryNavItem key={index} selected={!!item.selected}>
      {item.href ? (
        <PrimaryNavLink href={item.href} isDropdown={!!item.children?.length}>
          {item.label}
        </PrimaryNavLink>
      ) : (
        <PrimaryNavLabel isDropdown={!!item.children?.length}>
          {item.label}
        </PrimaryNavLabel>
      )}
      {item.children ? (
        <PrimaryNavList>{buildNavigationItems(item.children)}</PrimaryNavList>
      ) : (
        <></>
      )}
    </PrimaryNavItem>
  ))
}

function buildNavigation(security: Security, selectedIndex: number) {
  const data = buildNavigationTree(security, selectedIndex)
  return (
    <>
      <PrimaryNav name="Secondary navigation">
        <PrimaryNavList>{buildNavigationItems(data)}</PrimaryNavList>
      </PrimaryNav>
    </>
  )
}

interface NavigationItem {
  selected?: boolean
  href?: string
  label: string
  children?: NavigationItem[]
}
